<template>
  <div v-waiting.center.transparent="isLoading = { isLoading: loading }" class="dialog-wrapper" :class="[className]">
    <transition v-if="!hasSlot" appear name="fade">
      <div class="overlay text">
        <div class="dialog-box text">
          <div v-if="title" class="dialog-box__title">
            {{ title }}
          </div>
          <div class="dialog-box__main m-b-5">
            {{ content }}
          </div>
          <div v-if="buttons.length" class="dialog-box__controls">
            <div
              v-for="(btn, index) in buttons"
              :key="index"
              :class="btn.class ? ['btn', ...btn.class] : ['btn']"
              :style="{ minWidth: '40%' }"
              @click="btn.callback"
            >
              {{ btn.name }}
            </div>
          </div>
        </div>
      </div>
    </transition>
    <div v-else-if="hasMounted" class="overlay" :class="`bg-${componentBackground}`">
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { VNode } from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import waiting from '@/components/loading/waiting';

@Component({
  directives: {
    waiting,
  },
})
export default class Dialog extends Vue {
  @Prop({ type: Boolean, required: true }) readonly hasSlot!: boolean;
  @Prop({ type: String, required: false, default: '' }) readonly title!: string;
  @Prop({ type: [String, Object], required: true }) readonly content!: string | VNode;
  @Prop({ type: Array, default: () => [] }) readonly buttons!: any[];
  @Prop({ type: Boolean, required: false, default: false }) readonly loading!: boolean;
  @Prop({ type: String, required: false, default: '' }) readonly className!: string;
  @Prop({ type: String, default: 'white' }) readonly componentBackground!: 'white' | 'transparent' | 'grey' | 'blurry';

  hasMounted = false;

  mounted() {
    this.hasMounted = true;
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/base.scss';
.dialog-wrapper {
  height: 100vh;
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    &.text {
      background: rgba(black, 0.4);
    }

    @each $clr in white, transparent, grey {
      &.bg-#{'' + $clr} {
        background: $clr;
      }
    }

    &.bg-blurry {
      background: rgba(grey, 0.7);
    }
  }
  .dialog-box {
    font-size: $size-m;
    width: 28rem;
    background-color: $dialog-default-background;
    color: $dialog-default-text-color;
    text-align: center;
    line-height: 1.5;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 2px;
    box-shadow:
      0 3px 24px 0 rgba(0, 0, 0, 0.3),
      0 0 24px 0 rgba(0, 0, 0, 0.22);
    border-style: solid;
    border-width: 0.5px;
    border-image-source: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.8),
      rgba(255, 255, 255, 0.4) 5%,
      rgba(255, 255, 255, 0) 20%,
      rgba(255, 255, 255, 0)
    );
    padding: 3rem 3rem 1.5rem;
    border-radius: 1rem;
    &__title {
      margin-bottom: 20px;
      font-weight: bold;
      color: $dialog-default-title-color;
    }
    &__controls {
      display: flex;
      justify-content: space-between;
      margin-top: 2rem;
      width: 100%;
      .btn {
        background: $dialog-confirm-cancel-button-background;
        color: $dialog-confirm-cancel-button-text-color;
        height: 3.9rem;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        flex: 1;
        font-size: 1.6rem;
        cursor: pointer;
        &:not(:last-child) {
          margin-right: 1rem;
        }
        &:not(:first-child) {
          margin-left: 1rem;
        }
        &.primary {
          background-color: $dialog-confirm-success-button-background;
          color: $dialog-confirm-success-button-text-color;
          border-radius: 10px;
        }
      }
    }
  }
  .m-b-5 {
    margin-bottom: 5px;
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
